import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdWbSunny, MdNightlight, MdMenu, MdClose } from 'react-icons/md';
import { useAuth } from 'contexts/AuthContext';
import useBreakpoint from 'hooks/useBreakpoint';

/**
 * Top nav: logo + section links + theme toggle + sign-in / Dashboard CTA.
 *
 * For authenticated users the CTA points to either /admin or
 * /dashboard/brand/overview depending on `user.is_platform_admin` so a
 * platform admin doesn't get bounced through the empty Create-Brand
 * modal on their own marketing site.
 *
 * Below ~860px the links move into a drop-down panel behind a menu button.
 * They used to stay in the bar and simply run out of room — the logo and the
 * first link collided into "TechProOneFeatures" and the CTA fell off the
 * right edge. Four links, a wordmark, a toggle and a button stop fitting well
 * before phone width, so the switch happens at the point they stop fitting
 * rather than at a nominal "mobile" boundary.
 */

const LINKS = [
  ['Features', 'features'],
  ['How it works', 'how'],
  ['FAQ', 'faq'],
  ['Contact', 'contact'],
];

export default function Navbar({ t, scrolled, onLoginClick, isDark, onToggleTheme }) {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { isCompactNav, isNarrow } = useBreakpoint();
  const [open, setOpen] = useState(false);

  const dashboardPath = user?.is_platform_admin === true
    ? '/admin'
    : '/dashboard/brand/overview';

  // A menu left open while the viewport grows back to desktop would sit over
  // the page with no button to close it.
  useEffect(() => { if (!isCompactNav) setOpen(false); }, [isCompactNav]);

  // Escape closes it, which is the one keyboard affordance an overlay must
  // have.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const goTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const linkStyle = {
    background: 'none', border: 'none', color: t.textSub,
    fontSize: 14, fontWeight: 500, cursor: 'pointer', padding: 0,
    transition: 'color 0.2s', whiteSpace: 'nowrap',
  };

  const ctaButton = (
    isAuthenticated ? (
      <button
        onClick={() => navigate(dashboardPath)}
        style={{
          height: 38, padding: isNarrow ? '0 12px' : '0 18px', borderRadius: 10,
          background: 'linear-gradient(135deg, #7C3AED, #4F46E5)',
          color: '#fff', fontWeight: 600, fontSize: 14, border: 'none',
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        {isNarrow ? 'Open' : 'Dashboard'}
      </button>
    ) : (
      <button
        onClick={onLoginClick}
        style={{
          height: 38, padding: isNarrow ? '0 12px' : '0 20px', borderRadius: 10,
          background: 'linear-gradient(135deg, #7C3AED, #4F46E5)',
          color: '#fff', fontWeight: 600, fontSize: 14, border: 'none',
          cursor: 'pointer', whiteSpace: 'nowrap',
          transition: 'opacity 0.2s, transform 0.2s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'none'; }}
      >
        {isNarrow ? 'Start' : 'Get Started'}
      </button>
    )
  );

  const themeButton = (
    <button
      onClick={onToggleTheme}
      title={isDark ? 'Switch to Light mode' : 'Switch to Dark mode'}
      aria-label={isDark ? 'Switch to Light mode' : 'Switch to Dark mode'}
      style={{
        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
        background: t.surface, border: `1px solid ${t.border}`,
        color: isDark ? '#A78BFA' : '#7C3AED',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = t.surfaceHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = t.surface; }}
    >
      {isDark ? <MdWbSunny size={18} /> : <MdNightlight size={18} />}
    </button>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      background: (scrolled || open) ? t.navBg : 'transparent',
      backdropFilter: (scrolled || open) ? 'blur(14px)' : 'none',
      WebkitBackdropFilter: (scrolled || open) ? 'blur(14px)' : 'none',
      borderBottom: (scrolled || open) ? `1px solid ${t.navBorder}` : 'none',
      transition: 'background 0.3s ease, border-color 0.3s ease',
    }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        // Tighter gutter on a phone: 24px each side costs a seventh of a
        // 360px screen.
        padding: isCompactNav ? '0 16px' : '0 24px',
        display: 'flex', height: 64, alignItems: 'center',
        justifyContent: 'space-between', gap: 12,
      }}>
        <span
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            fontSize: isCompactNav ? 19 : 22, fontWeight: 800,
            background: 'linear-gradient(to right, #A78BFA, #60A5FA)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.5px', cursor: 'pointer', flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          TechProOne
        </span>

        {!isCompactNav && (
          <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
            {LINKS.map(([label, id]) => (
              <button
                key={label}
                onClick={() => goTo(id)}
                style={linkStyle}
                onMouseEnter={(e) => { e.currentTarget.style.color = t.text; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = t.textSub; }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: isNarrow ? 6 : 10, alignItems: 'center', flexShrink: 0 }}>
          {/* On the narrowest screens the toggle moves into the menu — it is
              the least urgent control in the bar, and keeping it there pushed
              the menu button off the edge where nobody could reach it. */}
          {!isNarrow && themeButton}
          {/* The CTA is the one thing worth keeping in the bar at every width —
              it is why the page exists. */}
          {ctaButton}
          {isCompactNav && (
            <button
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              style={{
                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                background: t.surface, border: `1px solid ${t.border}`,
                color: t.text, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {open ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          )}
        </div>
      </div>

      {isCompactNav && open && (
        <div style={{
          borderTop: `1px solid ${t.navBorder}`,
          padding: '8px 16px 16px',
          display: 'flex', flexDirection: 'column',
        }}>
          {isNarrow && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 8 }}>
              {themeButton}
            </div>
          )}
          {LINKS.map(([label, id]) => (
            <button
              key={label}
              onClick={() => goTo(id)}
              style={{
                ...linkStyle,
                textAlign: 'left',
                // Comfortably past the 44px touch target minimum.
                padding: '14px 4px',
                fontSize: 16,
                borderBottom: `1px solid ${t.navBorder}`,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
